import type { AnomalyDetail } from '../types/weld';
import type { WpsDrillDownVariant } from '../components/dashboard/drilldown/drillDownTypes';
import type { NdtIndicationRow, WeldDrillDownRow } from '../components/dashboard/drilldown/drillDownTypes';
import { glossaryText } from './weldFieldGlossary';
import { isNdtLogRejected } from './fetchNdtIndicationLogsApi';
import { complianceCounts, ndtBadgeFromLogs, rowKey } from './weldDrillDownModel';
import {
  aggregateFailuresByParameter,
  computeProductionImpact,
  type WpsViolationRecord,
} from './wpsViolationModel';

export interface DrillDownKpi {
  id: string;
  label: string;
  value: string;
  accent?: 'blue' | 'green' | 'red' | 'yellow';
  ndtDependent?: boolean;
  help?: string;
}

function avg(nums: number[]): number | null {
  if (!nums.length) return null;
  return nums.reduce((s, n) => s + n, 0) / nums.length;
}

function formatPct(n: number | null): string {
  return n != null ? `${n.toFixed(1)}%` : '—';
}

function formatSec(sec: number | null): string {
  if (sec == null) return '—';
  return sec < 60 ? `${sec.toFixed(1)}s` : `${(sec / 60).toFixed(2)} min`;
}

export function summarizeNdtCache(
  rows: WeldDrillDownRow[],
  ndtCache: Record<string, NdtIndicationRow[]>,
): {
  sampled: number;
  withRecords: number;
  withRejections: number;
  totalLogEntries: number;
  rejectedLogEntries: number;
  rejectionRatePct: number | null;
  topDefectType: string | null;
} {
  let withRecords = 0;
  let withRejections = 0;
  let totalLogEntries = 0;
  let rejectedLogEntries = 0;
  const defectCounts: Record<string, number> = {};

  rows.forEach((row) => {
    const logs = ndtCache[rowKey(row)];
    if (!logs) return;
    if (logs.length > 0) withRecords += 1;
    totalLogEntries += logs.length;
    const rejected = logs.some(isNdtLogRejected);
    if (rejected) withRejections += 1;
    logs.forEach((log) => {
      if (isNdtLogRejected(log)) rejectedLogEntries += 1;
      const dt = String(log.defectType ?? '').trim();
      if (dt) defectCounts[dt] = (defectCounts[dt] ?? 0) + 1;
    });
  });

  const sampled = Object.keys(ndtCache).length;
  const topDefectType =
    Object.entries(defectCounts).sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;

  return {
    sampled,
    withRecords,
    withRejections,
    totalLogEntries,
    rejectedLogEntries,
    rejectionRatePct: sampled > 0 ? (withRejections / sampled) * 100 : null,
    topDefectType,
  };
}

export function computeVariantKpis(
  variant: WpsDrillDownVariant,
  filteredRows: WeldDrillDownRow[],
  _allRows: WeldDrillDownRow[],
  ndtCache: Record<string, NdtIndicationRow[]>,
  ndtBatchLoading: boolean,
  violationsByRowKey: Record<string, WpsViolationRecord[]> = {},
  wpsBatchLoading = false,
): DrillDownKpi[] {
  const durations = filteredRows.map((r) => r.durationSec).filter((d): d is number => d != null);
  const qualities = filteredRows.map((r) => r.qualityScore).filter((q): q is number => q != null);
  const ndt = summarizeNdtCache(filteredRows, ndtCache);

  const hasNdtSample = ndt.sampled > 0;
  const ndtPlaceholder = ndtBatchLoading && !hasNdtSample ? '…' : '—';
  const wpsPlaceholder = wpsBatchLoading ? '…' : '—';

  if (variant === 'within') {
    return [
      { id: 'count', label: 'Welds within limit', value: String(filteredRows.length), accent: 'green' },
      { id: 'avgDuration', label: 'Avg duration', value: formatSec(avg(durations)), accent: 'blue' },
      {
        id: 'avgQuality',
        label: 'Avg quality score',
        value: avg(qualities) != null ? avg(qualities)!.toFixed(1) : '—',
        accent: 'blue',
        help: glossaryText('avg_quality_score'),
      },
      {
        id: 'ndtTotalLogs',
        label: 'Total NDT logs',
        value: ndtBatchLoading && !hasNdtSample ? ndtPlaceholder : String(ndt.totalLogEntries),
        accent: 'blue',
        ndtDependent: true,
      },
      {
        id: 'ndtWeldsWithRecords',
        label: 'Welds with NDT',
        value: ndtBatchLoading && !hasNdtSample ? ndtPlaceholder : String(ndt.withRecords),
        accent: 'blue',
        ndtDependent: true,
      },
    ];
  }

  if (variant === 'out') {
    const failedRows = filteredRows.filter((r) => r.outcome === 'fail');
    const allViolations = Object.values(violationsByRowKey).flat().filter(Boolean);
    const parameterBreakdown = aggregateFailuresByParameter(allViolations);
    const productionImpact = computeProductionImpact(failedRows, violationsByRowKey);
    const topParam = parameterBreakdown[0];

    return [
      { id: 'count', label: 'Welds out of limit', value: String(filteredRows.length), accent: 'red' },
      {
        id: 'passBreaches',
        label: 'Pass-level breaches',
        value: wpsBatchLoading && allViolations.length === 0
          ? wpsPlaceholder
          : String(productionImpact.affectedPassCount || allViolations.length),
        accent: 'yellow',
      },
      {
        id: 'topFailedParam',
        label: 'Top failed parameter',
        value: wpsBatchLoading && !topParam
          ? wpsPlaceholder
          : topParam
            ? `${topParam.parameter} - ${topParam.count}`
            : '-',
        accent: 'red',
      },
      {
        id: 'ndtRejected',
        label: 'NDT rejections',
        value: ndtBatchLoading && !hasNdtSample ? ndtPlaceholder : String(ndt.withRejections),
        accent: 'red',
        ndtDependent: true,
      },
      {
        id: 'topDefect',
        label: 'Top defect type',
        value: ndtBatchLoading && !hasNdtSample ? ndtPlaceholder : ndt.topDefectType ?? '—',
        accent: 'blue',
        ndtDependent: true,
      },
    ];
  }

  const compliance = complianceCounts(filteredRows);
  return [
    {
      id: 'within',
      label: 'Within limit',
      value: String(compliance.within),
      accent: 'green',
    },
    {
      id: 'out',
      label: 'Out of limit',
      value: String(compliance.out),
      accent: 'red',
    },
    {
      id: 'rate',
      label: 'Compliance rate',
      value: compliance.ratePct != null ? `${compliance.ratePct.toFixed(1)}%` : '—',
      accent: 'green',
    },
    {
      id: 'ndtTotalLogs',
      label: 'Total NDT logs',
      value: ndtBatchLoading && !hasNdtSample ? ndtPlaceholder : String(ndt.totalLogEntries),
      accent: 'blue',
      ndtDependent: true,
    },
    {
      id: 'avgQuality',
      label: 'Avg quality score',
      value: avg(qualities) != null ? avg(qualities)!.toFixed(1) : '—',
      accent: 'blue',
      help: glossaryText('avg_quality_score'),
    },
    {
      id: 'ndtRejectRate',
      label: 'NDT rejection rate',
      value: ndtBatchLoading && !hasNdtSample ? ndtPlaceholder : formatPct(ndt.rejectionRatePct),
      accent: 'red',
      ndtDependent: true,
    },
  ];
}

export function applyNdtCacheToRows(
  rows: WeldDrillDownRow[],
  ndtCache: Record<string, NdtIndicationRow[]>,
  selectedRow: WeldDrillDownRow | null,
  selectedLogs: NdtIndicationRow[] | null,
): WeldDrillDownRow[] {
  return rows.map((row) => {
    const key = rowKey(row);
    const cached = ndtCache[key];
    if (cached) {
      return {
        ...row,
        ndtBadge: ndtBadgeFromLogs(cached),
        ndtLogCount: cached.length,
      };
    }
    if (selectedRow && rowKey(selectedRow) === key && selectedLogs) {
      return {
        ...row,
        ndtBadge: ndtBadgeFromLogs(selectedLogs),
        ndtLogCount: selectedLogs.length,
      };
    }
    return row;
  });
}

export function aggregateNdtLogs(
  rows: WeldDrillDownRow[],
  ndtCache: Record<string, NdtIndicationRow[]>,
): NdtIndicationRow[] {
  const logs: NdtIndicationRow[] = [];
  rows.forEach((row) => {
    const cached = ndtCache[rowKey(row)];
    if (cached?.length) logs.push(...cached);
  });
  return logs;
}

export function aggregateAnomalies(rows: WeldDrillDownRow[]): AnomalyDetail[] {
  return rows.flatMap((row) => row.anomalies);
}

export function countWeldsWithAnomalies(rows: WeldDrillDownRow[]): number {
  return rows.filter((r) => r.anomalies.length > 0).length;
}
